import { useEffect, useMemo } from 'react';
import { styled } from 'goober';

import type { ProjectListing } from '@/api/v4/projects.api';
import { ListingStageName } from '@/features/listing-stage/types';
import type { TabConfig } from '@/ui/tabs/tabs';
import { Tabs } from '@/ui/tabs/tabs';

import { getDealListingsCount } from '../../helpers/get-deal-listings.count';
import { getStageListingsCount } from '../../helpers/get-stage-listings-count';

import { getActiveStageTabId } from './get-active-stage-tab-id';
import { ListingStageTabContent } from './listing-stage-tab-content';
import { useListingStagesToSwitchTabs } from './use-listing-stages-to-switch-tabs';
import type { TabStage } from './use-stage-tabs-config';
import { useStageTabsConfig } from './use-stage-tabs-config';
import { WorkmodeTabControl } from './workmode-tab-control';
import { useStageParam } from './use-stage-param';
import { useProjectListingsCount } from '../use-project-listings-count.query';
import { usePendingCompaniesCount } from '../../pending-companies/use-pending-companies-count.query';
import { useTranslation } from 'react-i18next';

export const StageManagementTable = ({
  listingsData,
  isLoading,
}: {
  listingsData: ProjectListing[];
  isLoading: boolean;
}) => {
  const { stageParam, deleteStageParam, setStageParam } = useStageParam();
  const tabStages = useStageTabsConfig();
  const { data: listingsCount } = useProjectListingsCount();
  const { data: pendingCompaniesCount } = usePendingCompaniesCount();
  const { t } = useTranslation('projects');
  const {
    getDropToDestination,
    setActiveFirstTabWithListings,
    setActiveTabByStageName,
  } = useListingStagesToSwitchTabs();

  useEffect(() => {
    if (isLoading || stageParam) {
      return;
    }

    if (listingsData?.length > 0) {
      return setActiveFirstTabWithListings();
    }

    if (tabStages?.length > 0) {
      return setActiveTabByStageName(ListingStageName.discovery);
    }

    return () => deleteStageParam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, stageParam]);

  const tabsConfig: TabConfig<TabStage['id']>[] = useMemo(
    () =>
      tabStages.map(tab => {
        const isDealTab = tab.name === ListingStageName.deal;

        return {
          id: tab.id,
          title: (
            <WorkmodeTabControl
              name={tab?.displayName ?? tab?.name}
              countIndicator={
                isDealTab
                  ? getDealListingsCount(listingsCount?.stagesCount ?? [])
                  : getStageListingsCount(tab, listingsCount?.stagesCount ?? [])
              }
            />
          ),
          content: (
            <ListingStageTabContent
              tab={tab}
              dragTo={getDropToDestination(tab)}
            />
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabStages, listingsCount, pendingCompaniesCount, t],
  );

  const activeStageTabId = useMemo(
    () => getActiveStageTabId(stageParam, tabStages),
    [stageParam, tabStages],
  );

  if (tabStages.length === 0 || !activeStageTabId) {
    return <WidgetWrapper data-testid="stage-management-table" />;
  }

  return (
    <WidgetWrapper data-testid="stage-management-table">
      <Tabs<TabStage['id']>
        tabsConfig={tabsConfig}
        activeTab={activeStageTabId}
        setActiveTab={tabId => setStageParam(tabId)}
        moveLastAtEnd
        padding="0 30px"
        withDividers
      />
    </WidgetWrapper>
  );
};

const WidgetWrapper = styled('div')`
  box-shadow: ${({ theme }) => theme.shadow.widgets};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors.basics.white};
`;
